/**
 * WelcomeScreen - Initial screen shown when no messages
 * 
 * Shows:
 * - Welcome icon and message
 * - Initial chat input
 * - Suggested prompts
 */

import { Heading, Text } from "@netapp/bxp-design-system-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../../../i18n/namespace";
import { SuggestedPromptGroup } from "../chat-components/SuggestedPrompt";
import AiAssistantIcon from "./AiAssistantIcon";
import chatStyles from "./ChatContainer.module.scss";
import ChatInput from "./ChatInput";

const WelcomeScreen = React.memo(({
    onSuggestedPromptClick,
    connectionState,
    sendMessage,
    isStreaming
}) => {
    const { t } = useTranslation(i18nNamespace);

    // Default suggested prompts
    const suggestedPrompts = [
        {
            id: '1',
            text: t("ai_prompt_1", "Summarize the critical alerts for my storage systems and suggest which to address first.")
        },
        {
            id: '2',
            text: t("ai_prompt_2", "Why does Restore jobs SLA have a warning?")
        },
        {
            id: '3',
            text: t("ai_prompt_3", "What's Ransomware Resilience and how do I enable it?")
        }
    ];

    // Determine if input should be disabled
    // For now, keep it enabled for better UX (will queue message if not connected)
    const isInputDisabled = false;

    return (
        <div className={chatStyles['welcome-section']}>
            {/* Icon */}
            <div className={chatStyles['icon-wrapper']}>
                <AiAssistantIcon width={55} height={59} />
            </div>

            {/* Welcome title */}
            <Heading level="16  " bold className={chatStyles['welcome-title']}>
                {t("ai_assistant_welcome", "Console assistant can help.")}
            </Heading>

            {/* Description */}
            <Text className={chatStyles['welcome-description']}>
                {t("ai_assistant_description", "Learn, analyze, and act. Console assistant helps you do more, faster.")}
            </Text>

            {/* Chat input */}
            <ChatInput
                onSend={sendMessage}
                disabled={isInputDisabled}
                placeholder={isStreaming ? t("ai_thinking", "Assistant is thinking...") : undefined}
            />

            {/* Suggested prompts heading */}
            <div className={chatStyles['recommended-prompts-heading']}>
                <Heading bold>
                    {t("ai_suggested_prompts", "Suggested prompts")}
                </Heading>
            </div>

            {/* Suggested prompts list */}
            <SuggestedPromptGroup
                suggestedPrompts={suggestedPrompts}
                onSuggestedPromptClick={onSuggestedPromptClick}
            />
        </div>
    );
});

WelcomeScreen.displayName = 'WelcomeScreen';

export default WelcomeScreen;
